import { useEffect, useRef, useState } from "react";
import {
  useAdbShellWebSocket,
  ShellOutputEntry,
} from "../components/hooks/useAdbShellWebSocket";
import DSDialog from "./system-design/DSDialog";
import DSDialogTitle from "./system-design/DSDialogTitle";
import DSDialogContent from "./system-design/DSDialogContent";
import DSDialogActions from "./system-design/DSDialogActions";
import DSAlert from "./system-design/DSAlert";
import DSBox from "./system-design/DSBox";
import DSStack from "./system-design/DSStack";
import DSTypography from "./system-design/DSTypography";
import DSTextField from "./system-design/DSTextField";
import DSButton from "./system-design/DSButton";
import DSPrimaryButton from "./system-design/DSPrimaryButton";
import DSSecondaryButton from "./system-design/DSSecondaryButton";
import DSErrorButton from "./system-design/DSErrorButton";
import useDeviceType from "../components/hooks/useDeviceType";

interface AdbShellStreamModalProps {
  deviceId: string;
  onClose: () => void;
}

const AdbShellStreamModal: React.FC<AdbShellStreamModalProps> = ({
  deviceId,
  onClose,
}) => {
  const uri = "wss://clock-socket-917257785826.us-central1.run.app/";
  const {
    start,
    stop,
    sendCommand,
    isConnected,
    error,
    loadingMessage,
    outputHistory,
    isExecuting,
    clearHistory,
  } = useAdbShellWebSocket({ uri, devId: deviceId });

  const { isSmallTablet } = useDeviceType();
  const [inputValue, setInputValue] = useState("");
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    start();
  }, []);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [outputHistory]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isExecuting) return;

    setCommandHistory((prev) => {
      const filtered = prev.filter((c) => c !== inputValue);
      return [inputValue, ...filtered].slice(0, 50);
    });
    setHistoryIndex(-1);

    sendCommand(inputValue);
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length === 0) return;
      const newIndex = Math.min(historyIndex + 1, commandHistory.length - 1);
      setHistoryIndex(newIndex);
      setInputValue(commandHistory[newIndex]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex <= 0) {
        setHistoryIndex(-1);
        setInputValue("");
      } else {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
      }
    }
  };

  const handleStop = () => {
    stop();
    onClose();
  };

  const handleClear = () => {
    clearHistory();
  };

  const renderEntry = (entry: ShellOutputEntry, idx: number) => {
    return (
      <DSBox key={idx} sx={{ mb: 1 }}>
        <DSBox sx={{ color: "#569cd6" }}>$ {entry.command}</DSBox>
        {entry.success ? (
          <>
            {entry.stdout && (
              <DSBox sx={{ whiteSpace: "pre-wrap" }}>{entry.stdout}</DSBox>
            )}
            {entry.stderr && (
              <DSBox sx={{ color: "#f44747", whiteSpace: "pre-wrap" }}>
                {entry.stderr}
              </DSBox>
            )}
            {entry.exitCode !== undefined && entry.exitCode !== 0 && (
              <DSBox sx={{ color: "#f44747" }}>
                exit code: {entry.exitCode}
              </DSBox>
            )}
          </>
        ) : (
          <DSBox sx={{ color: "#f44747" }}>
            Error: {entry.error || "Unknown error"}
          </DSBox>
        )}
      </DSBox>
    );
  };

  return (
    <DSDialog
      open
      onClose={handleStop}
      maxWidth="lg"
      fullWidth
      fullScreen={isSmallTablet}
    >
      <DSDialogTitle>ADB Shell</DSDialogTitle>
      <DSDialogContent>
        {loadingMessage && (
          <DSAlert severity="info" sx={{ mb: 2 }}>
            <strong>Status:</strong> {loadingMessage}
          </DSAlert>
        )}

        {error && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            <strong>Error:</strong> {error}
          </DSAlert>
        )}

        {isConnected && !loadingMessage && (
          <DSStack direction="column">
            <DSStack
              direction="row"
              spacing={1}
              sx={{ justifyContent: "flex-end", mb: 1 }}
            >
              <DSSecondaryButton size="small" onClick={handleClear}>
                Clear
              </DSSecondaryButton>
              <DSErrorButton size="small" onClick={handleStop}>
                Disconnect
              </DSErrorButton>
            </DSStack>

            <DSBox
              ref={outputRef}
              onClick={() => inputRef.current?.focus()}
              sx={{
                height: "60vh",
                overflowY: "auto",
                p: 2,
                backgroundColor: "#161616",
                color: "#00ff00",
                fontFamily: "monospace",
                fontSize: "13px",
                borderRadius: 2,
              }}
            >
              {outputHistory.map((entry, idx) => renderEntry(entry, idx))}
              {isExecuting && (
                <DSBox sx={{ color: "#888" }}>Executing...</DSBox>
              )}
            </DSBox>

            <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
              <DSStack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center" }}
              >
                <DSTypography
                  sx={{
                    fontFamily: "monospace",
                    py: "6px",
                    color: "text.secondary",
                    fontWeight: "bold",
                  }}
                >
                  $
                </DSTypography>
                <DSTextField
                  inputRef={inputRef}
                  type="text"
                  size="small"
                  fullWidth
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a shell command..."
                  disabled={isExecuting}
                  autoFocus
                  inputProps={{
                    style: {
                      fontFamily: "monospace",
                      fontSize: "13px",
                    },
                  }}
                />
                <DSPrimaryButton
                  type="submit"
                  disabled={isExecuting || !inputValue.trim()}
                  size="small"
                >
                  Run
                </DSPrimaryButton>
              </DSStack>
            </form>
          </DSStack>
        )}

        {!isConnected && !error && !loadingMessage && (
          <DSBox sx={{ textAlign: "center" }}>
            <DSTypography>Initializing connection to device...</DSTypography>
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={handleStop}>Close</DSSecondaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default AdbShellStreamModal;
